import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Swal from "sweetalert2";
import { useReactToPrint } from "react-to-print";
import ReactPaginate from "react-paginate";
import MobileNav from "../mobileNav/MobileNav";
import { fetchUserPermissions } from "../../reduxSlices/userSlice";
import PrintingHeader from "../printingHeader/PrintingHeader";
import printIcon from "../../assets/print-icon.svg";
import SearchInput from "../searchBar/SearchBar";
import "./inProgress.css";

const InProgress = () => {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [counts, setCounts] = useState(Array(0).fill(0));
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedToSend, setSelectedToSend] = useState([]);
  const [notePopUpRequestId, setNotePopUpRequestId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [displayData, setDisplayData] = useState([]);

  // Updated filters state to match API parameters
  const [filters, setFilters] = useState({
    TechnicianName: "",
    city: "",
    MaintenanceStartDate: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // New state for editing
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    maintenanceTypeId: "",
    TechnicianId: "", // Changed from technicianId to match API
  });
  const [technicians, setTechnicians] = useState([]);

  // Static maintenance types
  const maintenanceTypes = [
    { Id: 1, Name: "صيانه" },
    { Id: 2, Name: "تنظيف" },
  ];

  const itemsPerPageOptions = [25, 50, 100, 300, 400, totalItems];

  const dispatch = useDispatch();

  const { userPermissions, loadingPermissions } = useSelector(
    (state) => state.users
  );
  const url = `https://api.generalhome.sa/api/maintenance/GetInProgressRequests`;

  const maxLength = 10;
  const maxLengthNeib = 15;
  const maxLengthCity = 6;
  const maxLengthTech = 7;

  // Helper function to remove empty values from object
  const removeEmptyValues = (obj) => {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
      if (
        cleaned[key] === "" ||
        cleaned[key] === null ||
        cleaned[key] === undefined
      ) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  // Helper function to build search parameters
  const buildSearchParams = (term) => {
    if (!term.trim()) return {};

    // Check if the search term is a phone number (contains only digits, +, or spaces)
    const isPhoneNumber = /^[\d+\s]+$/.test(term);

    // Check if the search term is a name (contains Arabic/English letters)
    const isName = /[\u0600-\u06FFa-zA-Z]/.test(term);

    const searchParams = {};

    if (isPhoneNumber) {
      // If it looks like a phone number, search only by phone
      searchParams.PhoneNumber = term.replace(/\s/g, "");
    } else if (isName) {
      // If it looks like a name, search only by name
      searchParams.CustomerName = term;
    } else {
      // For mixed or unclear input, search by both but let backend handle it
      searchParams.CustomerName = term;
      searchParams.PhoneNumber = term;
    }

    return searchParams;
  };

  // Helper function to build filter parameters with MaintenanceEndDate logic
  const buildFilterParams = (filterParams) => {
    const cleanedFilters = removeEmptyValues(filterParams);

    // If MaintenanceStartDate is provided, set MaintenanceEndDate to the same value
    if (cleanedFilters.MaintenanceStartDate) {
      cleanedFilters.MaintenanceEndDate = cleanedFilters.MaintenanceStartDate;
    }

    return cleanedFilters;
  };

  const getAllAppointments = async (
    currentPage = 1,
    pageSize = itemsPerPageOptions,
    searchParams = {},
    filterParams = {}
  ) => {
    try {
      setLoading(true);

      const technicianId = localStorage.getItem("UserId");
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);

      // Build filter parameters with MaintenanceEndDate logic
      const finalFilterParams = buildFilterParams(filterParams);

      // Step 1️⃣: Initial payload to get total items count
      const initialPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: currentPage,
        PageSize: pageSize,
        ...searchParams,
        ...finalFilterParams,
      });

      const firstRes = await axios.post(url, initialPayload);
      const totalItemsFromAPI = firstRes.data.TotalItems || 0;

      // Step 2️⃣: If there are more items than the default page size, fetch them all
      const fullPayload = removeEmptyValues({
        MaintenanceAreaIds: maintenanceAreaIds,
        technicianId: technicianId,
        Page: 1, // always first page when showing all items
        PageSize: totalItemsFromAPI > 0 ? totalItemsFromAPI : pageSize,
        ...searchParams,
        ...finalFilterParams,
      });

      const res =
        totalItemsFromAPI > pageSize
          ? await axios.post(url, fullPayload)
          : firstRes;

      // Handle API response
      const totalPages = res.data.TotalPages || 1;
      const totalNumberOfPages =
        totalPages ||
        Math.ceil((res.data.TotalItems || 0) / fullPayload.PageSize);

      setPageCount(totalNumberOfPages);
      setDisplayData(res.data.Items);
      setTotalItems(res.data.TotalItems);
      setCurrentPage(0);
    } catch (err) {
      setError(err.message);
      setDisplayData([]);
      setTotalItems(0);
      setPageCount(0);
      setCurrentPage(0);
    } finally {
      setLoading(false);
    }
  };

  // Function to get technicians
  const getApprovedTechnicians = async () => {
    try {
      const maintenanceAreaIdsString =
        localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = maintenanceAreaIdsString
        .split(",")
        .map(Number);
      const url = `https://api.generalhome.sa/api/maintenance/GetApprovedTechnicians`;
      const res = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds,
      });
      setTechnicians(res.data);
    } catch (err) {
      console.error("Error fetching technicians:", err);
    }
  };

  // Pagination click handler
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    getAllAppointments(
      pageNumber,
      itemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  // Items per page change handler
  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    getAllAppointments(
      1,
      newItemsPerPage,
      buildSearchParams(searchTerm),
      filters
    );
  };

  // Render pagination component
  const renderPagination = () => (
    <ReactPaginate
      forcePage={currentPage}
      previousLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.94 4L6 4.94L9.05333 8L6 11.06L6.94 12L10.94 8L6.94 4Z"
            fill="black"
          />
        </svg>
      }
      nextLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.06 12L11 11.06L7.94667 8L11 4.94L10.06 4L6.06 8L10.06 12Z"
            fill="black"
            fillOpacity="0.3"
          />
        </svg>
      }
      breakLabel={"..."}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={3}
      onPageChange={handlePageClick}
      containerClassName={"pagination justify-content-end"}
      pageClassName={"page-item"}
      pageLinkClassName={"page-link"}
      previousClassName={"page-item"}
      previousLinkClassName={"page-link"}
      nextClassName={"page-item"}
      nextLinkClassName={"page-link"}
      breakClassName={"page-item"}
      breakLinkClassName={"break-link"}
      activeClassName={"active"}
    />
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  // Handle search submit (when user clicks search button or presses enter)
  const handleSearchSubmit = () => {
    const searchParams = buildSearchParams(searchTerm);
    getAllAppointments(1, itemsPerPage, searchParams, filters);
  };

  // Handle enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Reset to first page when applying filters
    getAllAppointments(1, itemsPerPage, buildSearchParams(searchTerm), filters);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      TechnicianName: "",
      city: "",
      MaintenanceStartDate: "",
    });
    setSearchTerm(""); // Clear search term when resetting filters
    // Reset to first page and fetch without filters
    getAllAppointments(1, itemsPerPage);
  };

  // New function to handle edit button click
  const handleEditClick = (appointment) => {
    if (editingRequestId === appointment.RequestId) {
      // If already editing this row, save changes
      handleSaveEdit(appointment);
    } else {
      // Start editing this row
      setEditingRequestId(appointment.RequestId);
      setEditFormData({
        maintenanceTypeId: appointment.MaintenanceTypeId?.toString() || "",
        technicianId: appointment.TechnicianId || "",
      });
    }
  };

  // New function to handle input changes

  // New function to handle input changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async (appointment) => {
    try {
      // Validate form
      if (!editFormData.maintenanceTypeId || !editFormData.TechnicianId) {
        toast.error("يرجى ملء جميع الحقول", {
          position: "top-right",
          autoClose: 2000,
          theme: "light",
        });
        return;
      }

      console.log("Sending payload:", {
        RequestId: appointment.RequestId,
        TechnicianId: editFormData.TechnicianId,
        MaintenanceTypeId: parseInt(editFormData.maintenanceTypeId),
      });

      const response = await axios.post(
        "https://api.generalhome.sa/api/maintenance/UpdateMaintenanceTypeAndTechnicianName",
        {
          RequestId: appointment.RequestId,
          TechnicianId: editFormData.TechnicianId,
          MaintenanceTypeId: parseInt(editFormData.maintenanceTypeId),
        }
      );

      // Check both HTTP status and API response status
      if (response.status === 200 && response.data.status === true) {
        toast.success("تم التعديل بنجاح", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });

        // Refresh data after editing
        getAllAppointments(
          currentPage + 1,
          itemsPerPage,
          buildSearchParams(searchTerm),
          filters
        );

        setEditingRequestId(null);
        setEditFormData({
          maintenanceTypeId: "",
          TechnicianId: "",
        });
      } else {
        // API returned status: false
        const errorMessage =
          response.data.Message || "فشل في التعديل، يرجى المحاولة مرة أخرى";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        console.error("API returned error:", response.data);
      }
    } catch (error) {
      console.error("Error updating appointment:", error);

      let errorMessage = "فشل في التعديل";

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.Message) {
          errorMessage = error.response.data.Message;
        } else {
          errorMessage = `خطأ في الخادم: ${error.response.status}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = "لا يوجد اتصال بالخادم";
      } else {
        // Something else happened
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  // New function to cancel editing
  const handleCancelEdit = () => {
    setEditingRequestId(null);
    setEditFormData({
      maintenanceTypeId: "",
      TechnicianId: "",
    });
  };

  // Rest of your existing functions
  const validateForm = () => {
    let isValid = true;
    let errorMessages = [];
    if (selectedToSend.length === 0) {
      isValid = false;
      setError("يجب اختيار قطعة غيار واحدة على الأقل");
    }
    for (const optionId of selectedToSend) {
      if (counts[optionId] === undefined || counts[optionId] < 0) {
        isValid = false;
        setError(`الكمية غير صحيحة لقطعة الغيار`);
      }
    }
    if (!isValid) {
      console.error("Validation errors:", errorMessages);
      return false;
    }
    return true;
  };

  const moveDataToMaintainceDone = async (appointment, condition) => {
    if (validateForm()) {
      try {
        const selectedParts = selectedToSend.map((Id) => ({
          AirCondMaintPartsId: Id,
          NumberOfMaintParts: counts[Id] || 0,
        }));
        await axios.post(
          "https://api.generalhome.sa/api/maintenance/FinishMaintenance",
          {
            RequestId: selectedRequestIdRef.current,
            ProcessingTypeId: condition,
            UserId: localStorage.getItem("UserId"),
            AirCondMaintPartsIds: selectedParts,
          }
        );
        toast.success("تم انهاء الطلب بنجاح", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });

        setShowPopup(false);
        setSelectedRequestId(null);

        // Refresh data after moving
        getAllAppointments(
          currentPage + 1,
          itemsPerPage,
          buildSearchParams(searchTerm),
          filters
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  const moveDataToCancelled = async (appointment, condition) => {
    try {
      const parsedData = JSON.parse(JSON.stringify(appointment));
      const requestId = parsedData.RequestId;
      Swal.fire({
        title: "هل أنت متأكد من الغاء الطلب؟",
        text: "لن تستطيع استعاده الطلب بعد الالغاء!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, move it!",
        cancelButtonText: "No, cancel!",
        reverseButtons: true,
        backdrop: `rgba(0,0,123,0.4)`,
        customClass: {
          container: "my-swal",
        },
      }).then(async (willMove) => {
        if (willMove.value) {
          try {
            await axios.post(
              "https://api.generalhome.sa/api/maintenance/CancelRequest",
              {
                RequestId: requestId,
                ProcessingTypeId: condition,
                UserId: localStorage.getItem("UserId"),
              }
            );
            toast.success("تم الغاء الطلب بنجاح", {
              position: "top-right",
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });

            // Refresh data after moving
            getAllAppointments(
              currentPage + 1,
              itemsPerPage,
              buildSearchParams(searchTerm),
              filters
            );
          } catch (error) {
            console.error("Error moving request:", error);
          }
        } else if (willMove.dismiss === Swal.DismissReason.cancel) {
          Swal.fire({
            title: "Cancelled",
            text: "request didn't move",
            icon: "info",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "OK",
          });
        }
      });
    } catch (error) {
      console.error("Error parsing data:", error);
    }
  };

  const getAllSpareParts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        "https://api.generalhome.sa/api/AirCondMaintParts"
      );
      setOptions(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  const selectedRequestIdRef = useRef(null);
  const appointmentRef = useRef(null);

  const togglePopup = (appointment) => {
    setSelectedAppointment(appointment);
    appointmentRef.current = appointment;
    setSelectedRequestId(appointment.RequestId);
    selectedRequestIdRef.current = appointment.RequestId;
    setShowPopup(!showPopup);
  };

  const increase = (index) => {
    setCounts((prevCounts) => {
      const updatedCounts = [...prevCounts];
      updatedCounts[index] = (updatedCounts[index] || 0) + 1;
      return updatedCounts;
    });
  };

  const decrease = (index) => {
    setCounts((prevCounts) => {
      const updatedCounts = [...prevCounts];
      updatedCounts[index] = Math.max((updatedCounts[index] || 0) - 1, 0);
      return updatedCounts;
    });
  };

  const handleCheckboxChange = (Id) => {
    const newSelected = [...selectedToSend];
    if (newSelected.includes(Id)) {
      const index = newSelected.indexOf(Id);
      newSelected.splice(index, 1);
    } else {
      newSelected.push(Id);
    }
    setSelectedToSend(newSelected);
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  const handleTogglePopup = (appointment) => {
    if (notePopUpRequestId === appointment.RequestId) {
      setNotePopUpRequestId(null);
    } else {
      setNotePopUpRequestId(appointment.RequestId);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("UserId");

    if (userId) {
      dispatch(fetchUserPermissions(userId));
    } else {
      console.error("User ID not found in local storage");
    }

    getAllAppointments(1, itemsPerPage);
    getApprovedTechnicians();
    getAllSpareParts();
  }, [dispatch]);

  const formatACTypes = (maintenanceDetails) => {
    if (!maintenanceDetails || maintenanceDetails.length === 0) return "--";
    return maintenanceDetails.map((detail) => detail.ACTypeName).join("، ");
  };

  const CanWriteProgressRequests =
    userPermissions?.CanWriteProgressRequests === "true";

  return (
    <>
      <MobileNav />
      <section className="newAppointment finished mt-0">
        <Helmet>
          <meta charSet="utf-8" />
          <title>المواعيد التي تم جدولتها</title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/inProgress"
          />
        </Helmet>
        {loading || loadingPermissions ? (
          <div className="spinner-container">
            <ClipLoader
              className="loader"
              loading={loading || loadingPermissions}
              size={100}
              aria-label="Loading Spinner"
              data-testid="loader"
              color="#00b1ff"
            />
          </div>
        ) : (
          <div className="newAppointment-container" ref={componentRef}>
            <PrintingHeader title="طلبات  قيد التنفيذ" count={totalItems} />
            <SearchInput
              searchTerm={searchTerm}
              handleSearch={handleSearchChange}
              handleSearchSubmit={handleSearchSubmit}
              handleKeyPress={handleKeyPress}
              setSearchTerm={setSearchTerm}
              placeholder="ابحث عن اسم العميل أو رقم الجوال"
            />
            <div className="d-flex justify-content-between info">
              <div className="info-header">
                <h2>طلبات قيد التنفيذ</h2>
                <h3>{totalItems}</h3>
              </div>
              <div className="print-icon-holder">
                <img
                  src={printIcon}
                  alt="printIcon"
                  className="print-icon"
                  onClick={handlePrint}
                />
              </div>
            </div>

            {/* Filter form */}
            <div className="form-holder">
              <h5>تصفية حسب</h5>
              <form className="mt-4 filter-form" onSubmit={handleFilterSubmit}>
                <div className="filter-group">
                  <div className="sub-filter-group">
                    <input
                      className="w-100"
                      type="text"
                      name="TechnicianName"
                      placeholder="ابحث باسم الفني"
                      value={filters.TechnicianName}
                      onChange={handleFilterChange}
                      list="technician-suggestions"
                    />
                    <input
                      className="w-100"
                      type="text"
                      name="city"
                      placeholder="ابحث بالمدينة"
                      value={filters.city}
                      onChange={handleFilterChange}
                    />
                  </div>
                  <div className="sub-filter-group">
                    <input
                      type="date"
                      name="MaintenanceStartDate"
                      value={filters.MaintenanceStartDate}
                      onChange={handleFilterChange}
                      style={{
                        color: "#777",
                        backgroundColor: "#white", // any dark color so text is readable
                      }}
                    />
                    <div className="filter-button-container">
                      <button
                        type="button"
                        className="reset-button"
                        onClick={handleResetFilters}
                        disabled={loading}
                      >
                        إعادة تعيين
                      </button>
                      <button type="submit" disabled={loading}>
                        {loading ? "جاري التصفية..." : "تصفية"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div className="table-responsive-sm overflow-auto">
              {displayData?.length > 0 ? (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th scope="row">إسم العميل</th>
                      <th scope="row">رقم الهاتف</th>
                      <th scope="row">المدينه</th>
                      <th scope="row" className="district-th">
                        الحي
                      </th>
                      <th scope="row">نوع المكيف</th>
                      <th scope="row">نوع الصيانة</th>
                      <th className="errorType-th">الملاحظات</th>
                      <th scope="row" className="date-th">
                        تاريخ الصيانه
                      </th>
                      <th scope="row" className="tech-th">
                        الفني
                      </th>
                      <th scope="row" className="text-center">
                        التحكم
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {displayData.map((appointment) => (
                      <tr key={appointment.RequestId}>
                        <td
                          style={{
                            position: "relative",
                            padding: 0,
                            borderTopRightRadius: "8px",
                            borderBottomRightRadius: "8px",
                          }}
                        >
                          <div
                            style={
                              appointment.MaintenanceTypeId === 2
                                ? {
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                    borderTopRightRadius: "8px",
                                    borderBottomRightRadius: "8px",
                                    borderRight: "4px solid #007bff",
                                    padding: "8px",
                                    display: "flex",
                                  }
                                : {
                                    padding: "8px",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                  }
                            }
                          >
                            {appointment.CustomerName &&
                            appointment.CustomerName.length > maxLength
                              ? `${appointment.CustomerName.substring(
                                  0,
                                  maxLength
                                )}...`
                              : appointment.CustomerName}
                          </div>
                        </td>

                        <td>
                          <a
                            href={`https://api.whatsapp.com/send?phone=+966${appointment.PhoneNumber.replace(
                              /[^\d+]/g,
                              ""
                            )}&text=Hello%2C%20I'm%20interested%20in%20your%20product`}
                            target="__blank"
                            rel="noopener noreferrer"
                          >
                            {appointment.PhoneNumber}
                          </a>
                        </td>

                        <td>
                          {appointment.City &&
                          appointment.City.length > maxLengthCity
                            ? `${appointment.City.substring(
                                0,
                                maxLengthCity
                              )}...`
                            : appointment.City}
                        </td>

                        <td className="district-td">
                          {appointment.Neighborhood &&
                          appointment.Neighborhood.length > maxLengthNeib
                            ? `${appointment.Neighborhood.substring(
                                0,
                                maxLengthNeib
                              )}...`
                            : appointment.Neighborhood}
                        </td>

                        <td>
                          {formatACTypes(appointment.MaintenanceRequestDetails)
                            ?.length > maxLength
                            ? `${formatACTypes(
                                appointment.MaintenanceRequestDetails
                              ).substring(0, maxLength)}...`
                            : formatACTypes(
                                appointment.MaintenanceRequestDetails
                              )}
                        </td>

                        <td>
                          {editingRequestId === appointment.RequestId ? (
                            <select
                              name="maintenanceTypeId"
                              value={editFormData.maintenanceTypeId}
                              onChange={handleEditChange}
                              className="form-control"
                            >
                              <option value="">اختر نوع الخدمة</option>
                              <option value={1}>صيانه</option>
                              <option value={2}>تنظيف</option>
                            </select>
                          ) : (
                            maintenanceTypes.find(
                              (type) =>
                                type.Id === appointment.MaintenanceTypeId
                            )?.Name || appointment.MaintenanceTypeId
                          )}
                        </td>

                        <td className="errorType-td">
                          {appointment.ErrorType?.trim()
                            ? appointment.ErrorType.length > maxLength
                              ? `${appointment.ErrorType.substring(
                                  0,
                                  maxLength
                                )}...`
                              : appointment.ErrorType
                            : "No note"}

                          {appointment.ErrorType?.trim() &&
                            appointment.ErrorType.length > maxLength && (
                              <span
                                onClick={() => handleTogglePopup(appointment)}
                                className="see-more-link"
                              >
                                المزيد
                              </span>
                            )}

                          {appointment.ErrorType?.trim() &&
                            notePopUpRequestId === appointment.RequestId && (
                              <div className="notePopup">
                                <div className="notePopup-content">
                                  <button
                                    onClick={() =>
                                      handleTogglePopup(appointment)
                                    }
                                    className="close-btn d-flex"
                                  >
                                    X
                                  </button>
                                  <h3>الملاحظات</h3>
                                  <p>{appointment.ErrorType}</p>
                                </div>
                              </div>
                            )}
                        </td>

                        <td className="date-td">
                          {appointment.MaintenanceStartDate}
                        </td>

                        <td className="tech-td">
                          {editingRequestId === appointment.RequestId ? (
                            <select
                              name="TechnicianId"
                              value={editFormData.TechnicianId}
                              onChange={handleEditChange}
                              className="form-control"
                            >
                              <option value="">اختر الفني</option>
                              {technicians.map((tech) => (
                                <option
                                  key={tech.TechnicianId}
                                  value={tech.TechnicianId}
                                >
                                  {tech.Username}
                                </option>
                              ))}
                            </select>
                          ) : appointment.TechnicianName &&
                            appointment.TechnicianName.length >
                              maxLengthTech ? (
                            `...${appointment.TechnicianName.substring(
                              0,
                              maxLengthTech
                            )}`
                          ) : (
                            appointment.TechnicianName
                          )}
                        </td>

                        <td className="btn-box">
                          {/* <div className="firstBtnBox d-flex"> */}
                          <button
                            className="btn btn-danger"
                            onClick={() => moveDataToCancelled(appointment, 6)}
                            disabled={!CanWriteProgressRequests}
                          >
                            إلغاء
                          </button>
                          <button
                            className="finish-button"
                            key={appointment.RequestId}
                            onClick={() => togglePopup(appointment)}
                            disabled={!CanWriteProgressRequests}
                          >
                            إنهاء
                          </button>
                          {/* </div> */}

                          {/* <div className="seconedBtnBox"> */}
                          <button
                            className={`btn ${
                              editingRequestId === appointment.RequestId
                                ? "btn-success"
                                : "btn-warning"
                            }`}
                            onClick={() => handleEditClick(appointment)}
                            disabled={!CanWriteProgressRequests}
                          >
                            {editingRequestId === appointment.RequestId
                              ? "حفظ"
                              : "تعديل"}
                          </button>

                          {editingRequestId === appointment.RequestId && (
                            <button
                              className="btn btn-secondary"
                              onClick={handleCancelEdit}
                            >
                              إلغاء
                            </button>
                          )}

                          <Link
                            className="w-100"
                            to={`/dashboard/inProgress/${appointment.RequestId}`}
                          >
                            <button className="w-100">تفاصيل</button>
                          </Link>
                          {/* </div> */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                // ✅ Show this instead of the entire table when there's no data
                <div className="no-data-card">
                  <div className="no-data-content">
                    <div className="no-data-icon">📭</div>
                    <h3>لا توجد طلبات</h3>
                    <p>لا توجد بيانات لعرضها حالياً</p>
                  </div>
                </div>
              )}
            </div>

            {showPopup && selectedRequestId && selectedAppointment && (
              <div className="layout">
                <div className="finished-popup">
                  <div className="popup-content">
                    <div className="headliner d-flex justify-content-between mb-3">
                      <h3>قطع الغيار</h3>
                      <div className="btn-box d-flex g-2 flex-row">
                        <button
                          className="btn btn-danger"
                          onClick={() => closePopup()}
                        >
                          خروج
                        </button>
                        <button
                          className="finish-button"
                          onClick={() => {
                            moveDataToMaintainceDone(appointmentRef.current, 5);
                          }}
                        >
                          إنهاء
                        </button>
                      </div>
                    </div>
                    <div className="table-container overflow-auto">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>اختار</th>
                            <th>قطع الغيار</th>
                            <th>العدد</th>
                          </tr>
                        </thead>
                        <tbody>
                          {options.map((option) => {
                            return (
                              <tr key={option.Id}>
                                <td>
                                  <input
                                    key={option.Id}
                                    type="checkbox"
                                    checked={selectedToSend.includes(option.Id)}
                                    onChange={() =>
                                      handleCheckboxChange(option.Id)
                                    }
                                  />
                                </td>
                                <td>{option.Name}</td>
                                <td>
                                  <div className="counter">
                                    <button onClick={() => decrease(option.Id)}>
                                      -
                                    </button>
                                    <span>
                                      {counts[option.Id] !== undefined
                                        ? counts[option.Id].toString()
                                        : 0}
                                    </span>
                                    <button onClick={() => increase(option.Id)}>
                                      +
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default InProgress;
